import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type CSSProperties, type ReactNode } from 'react'
import { ApiError, type PostValue } from '../../api/client'
import { autonomyPost, autonomyQuery, type Answer, type AutonomyPage as Page, type Row } from '../../api/settingsAutonomy'
import { Spinner } from '../../ui/Spinner'
import { AutonomyResponsePanel } from './AutonomyResponsePanel'
import { AutonomyDeskPanel } from './AutonomyDeskPanel'
import { MailIntakePanel } from './MailIntakePanel'

/**
 * Settings -> Policies -> Agent Autonomy, element cu element fata de
 * `views/version-3/settings/autonomy/index.php` (+ `response.php`,
 * `desk.php`): intrerupatorul general cu notificarea, auto-diagnoza si
 * plafonul; raspunsul la alarme; biroul de service; senzorii; "Ce face
 * Autonomy"; biblioteca de reguli; regulile (cu suprascrierea firmei);
 * editorul de regula cu fereastra de mentenanta si playbook; activitatea
 * recenta. Textele si culorile sunt ale machetei; mesajele de dupa fiecare
 * salvare la fel ('...', 'Saved', 'Failed', si `error`-ul serverului).
 */
export const POSTURE: Record<string, [string, string, string, string]> = {
  shadow: ['Shadow', '#6b7480', '#eef0f2', 'only logs what it would have done'],
  propose: ['Propose', '#1f5f8b', '#e3f0fa', 'drafts a proposal; a person approves it'],
  remediate: ['Remediate', '#b42318', '#fdecea', 'runs the catalog action there and then'],
}
const SEV_COLOR: Record<string, string> = { critical: '#b42318', major: '#b54708', minor: '#6b7480', info: '#6b7480' }
const DAYS: [string, string][] = [['1', 'Mon'], ['2', 'Tue'], ['3', 'Wed'], ['4', 'Thu'], ['5', 'Fri'], ['6', 'Sat'], ['7', 'Sun']]

const pill = (bg: string, fg: string): CSSProperties => ({ background: bg, color: fg, padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600 })
const muted: CSSProperties = { fontSize: 12 }
export const s = (v: unknown): string => String(v ?? '')
export const n = (v: unknown): number => Number(v ?? 0)

/** `$m.text('...')`-ul de langa fiecare buton: gri cat se cere, verde/rosu dupa. */
export function useMsg(): [ReactNode, (text: string, tone: 'wait' | 'ok' | 'bad') => void] {
  const [state, set] = useState<{ text: string; tone: 'wait' | 'ok' | 'bad' } | null>(null)
  const node = state ? <span style={{ fontSize: 12, color: state.tone === 'ok' ? '#2e7d32' : state.tone === 'bad' ? '#b42318' : '#6b7480' }}>{state.text}</span> : null
  return [node, (text, tone) => set({ text, tone })]
}

export function post(path: string, fields: Record<string, PostValue>, say: (t: string, tone: 'wait' | 'ok' | 'bad') => void, done?: (r: Answer) => void, okText = 'Saved') {
  say('...', 'wait')
  void autonomyPost(path, fields)
    .then((r) => {
      if (r.ok) {
        say(okText, 'ok')
        done?.(r)
      } else say(r.error || 'Failed', 'bad')
    })
    .catch((e: unknown) => say(e instanceof ApiError && e.message ? e.message : 'Failed', 'bad'))
}

export function AutonomyPage() {
  const client = useQueryClient()
  const params = new URLSearchParams(window.location.search)
  const scope = params.get('scope') ?? ''
  const dscope = params.get('dscope') ?? ''
  const page = useQuery(autonomyQuery(scope, dscope))
  const [editing, setEditing] = useState<Row | null>(null)

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> Forbidden
      </div>
    )
  }
  if (!page.data) return <Spinner />
  const d = page.data
  const reload = () => client.invalidateQueries({ queryKey: ['settings', 'autonomy'] })
  const mayWrite = d.isGlobal || d.canScope

  const deskPaused = d.deskPaused?.paused === true

  return (
    <div className="row">
      <div className="col-md-11 col-md-offset-1">
        {deskPaused && (
          <div className="panel panel-default" id="odau-desk-paused" style={{ borderLeft: '4px solid #b42318' }}>
            <div className="panel-body" style={{ background: '#fdecea' }}>
              <strong style={{ color: '#b42318' }}>The AI desk is paused</strong>
              {d.deskPaused?.by ? <> by user #{d.deskPaused.by}</> : null}
              {d.deskPaused?.at ? <> since {d.deskPaused.at}</> : null}
              {d.deskPaused?.reason ? <>: {d.deskPaused.reason}</> : null}
              . No new run starts; machine tools refuse; desk replies stay queued. Mailboxes are still read.
            </div>
          </div>
        )}
        {!d.ready && (
          <div className="panel panel-default" style={{ borderLeft: '4px solid #b42318' }}>
            <div className="panel-body" style={{ background: '#fdecea' }}>
              <strong style={{ color: '#b42318' }}>The Autonomy schema has not been applied.</strong> Apply it in one click from{' '}
              <a href="/app/settings/change_log">Change Log &rarr; Database migrations</a>, then come back here.
            </div>
          </div>
        )}

        <SwitchPanel d={d} onChange={reload} />

        <AutonomyResponsePanel r={d.response} isGlobal={d.isGlobal} companyId={d.companyId} companyName={d.companyName} onChange={reload} />
        <AutonomyDeskPanel k={d.desk} isGlobal={d.isGlobal} companyId={d.companyId} companyName={d.companyName} onChange={reload} />
        <MailIntakePanel scope={d.desk.scope} companyId={d.companyId} companyName={d.companyName} />

        <SensorsPanel d={d} />

        <div className="panel panel-default" style={{ borderLeft: '4px solid #1f5f8b', marginTop: 18 }}>
          <div className="panel-body">
            <strong style={{ fontSize: 14 }}>Ce face Autonomy</strong>
            <p style={{ margin: '6px 0 8px' }}>
              A <strong>rule</strong> ties a pattern in the log to a pre-approved action. In words:{' '}
              <em>&bdquo;if I see <strong>this</strong>, <strong>this many times</strong> within <strong>this many minutes</strong> on the same machine, then <strong>do that</strong>&rdquo;</em>.
            </p>
            <p style={{ margin: '0 0 8px' }} className="text-muted">
              For example: <em>&bdquo;if the backup service dies three times in 30 minutes, restart it&rdquo;</em> &mdash; pattern: module <code>proactivitee</code>, severity <code>critical</code>, keyword <code>backup</code>; threshold: 3 in 30 min; action: the restart command from the catalog.
            </p>
            <p style={{ margin: 0 }}>
              <strong>Posture</strong> decides how far it goes, and it is the choice that matters most:{' '}
              <span style={{ color: '#6b7480' }}><strong>Shadow</strong> only writes down what it WOULD have done</span> &middot;{' '}
              <span style={{ color: '#1f5f8b' }}><strong>Propose</strong> prepares a proposal for you to approve</span> &middot;{' '}
              <span style={{ color: '#b42318' }}><strong>Remediate</strong> runs it there and then</span>. Always start on Shadow: leave it a few days, read in &bdquo;Recent&rdquo; what it would have done, and only then move up. The command ALWAYS comes from the catalog of pre-approved actions, never written by the agent.
            </p>
          </div>
        </div>

        <LibraryPanel d={d} onChange={reload} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '18px 0 8px' }}>
          <strong style={{ fontSize: 15 }}>Rules</strong>
          {mayWrite ? (
            <button type="button" className="btn btn-primary btn-sm" id="odau-add" onClick={() => setEditing({ posture: 'shadow', match_severity: 'critical' })}>
              + New rule{d.isGlobal ? '' : ` (for ${d.companyName})`}
            </button>
          ) : (
            <span className="text-muted" style={muted}>per-company rules need the schema migration; until then only a Global Admin writes them</span>
          )}
        </div>
        {d.rules.length === 0 && (
          <div className="panel panel-default">
            <div className="panel-body text-muted">
              No rules yet.{' '}
              {mayWrite ? (
                <>Press &bdquo;New rule&rdquo; and start on <strong>Shadow</strong> &mdash; it will not touch a thing until you say so.</>
              ) : (
                'The Global Admin has not written any rules yet.'
              )}
            </div>
          </div>
        )}
        {d.rules.map((rule) => (
          <RuleCard key={n(rule.ID)} d={d} rule={rule} onEdit={() => setEditing(rule)} onChange={reload} />
        ))}

        {mayWrite && editing && <RuleEditor d={d} rule={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void reload() }} />}

        <div style={{ margin: '18px 0 8px' }}><strong style={{ fontSize: 15 }}>Recent activity</strong></div>
        <div className="panel panel-default">
          <div className="panel-body">
            {d.recent.length === 0 ? (
              <span className="text-muted" style={muted}>Nothing has fired yet.</span>
            ) : (
              <table className="table table-condensed" style={{ fontSize: 12, margin: 0 }}>
                <thead><tr><th>When</th><th>Rule</th><th>Posture</th><th>Device</th><th>Proposal</th></tr></thead>
                <tbody>
                  {d.recent.map((r, i) => {
                    const pm = POSTURE[s(r.posture)] ?? POSTURE.shadow
                    return (
                      <tr key={i}>
                        <td style={{ whiteSpace: 'nowrap' }}>{s(r.fired_at)}</td>
                        <td>{s(r.rule_name)}</td>
                        <td><span style={{ color: pm[1] }}>{pm[0]}</span></td>
                        <td>{s(r.device) || `#${n(r.device_id)}`}</td>
                        <td>{n(r.draft_id) ? <a href="/app/change">#{n(r.draft_id)}</a> : <span className="text-muted">—</span>}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function SwitchPanel({ d, onChange }: { d: Page; onChange(): void }) {
  const [switchMsg, saySwitch] = useMsg()
  const [pauseMsg, sayPause] = useMsg()
  const [notifyMsg, sayNotify] = useMsg()
  const [diagMsg, sayDiag] = useMsg()
  const [capMsg, sayCap] = useMsg()
  const [email, setEmail] = useState(d.notifyEmail)
  const [cap, setCap] = useState(String(d.dailyCap ?? 50))
  const on = d.switchedOn
  const deskOnPause = d.deskPaused?.paused === true
  return (
    <div className="panel panel-default" style={{ borderLeft: `4px solid ${on ? '#2e7d32' : '#b54708'}` }}>
      <div className="panel-body" style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 14 }}>Autonomy master switch</strong>
        <span style={pill(on ? '#e6f4e6' : '#fdf3dd', on ? '#2e7d32' : '#8a6116')}>{on ? 'ON' : 'OFF'}</span>
        {d.isGlobal ? (
          <>
            <button type="button" className={`btn btn-sm ${on ? 'btn-default' : 'btn-success'}`} id="odau-switch" onClick={() => post('/switch', { on: on ? '0' : '1' }, saySwitch, () => onChange(), on ? 'OFF' : 'ON')}>
              {on ? 'Turn OFF' : 'Turn ON'}
            </button>
            {switchMsg}
            <button
              type="button"
              className={`btn btn-sm ${deskOnPause ? 'btn-success' : 'btn-danger'}`}
              id="odau-desk-pause"
              onClick={() => {
                if (deskOnPause) {
                  post('/desk/pause', { on: '0', reason: '' }, sayPause, () => onChange(), 'Resumed')
                  return
                }
                const reason = window.prompt('Why is the AI desk paused?', '') ?? ''
                post('/desk/pause', { on: '1', reason }, sayPause, () => onChange(), 'Paused')
              }}
            >
              {deskOnPause ? 'Resume the AI' : 'Pause the AI'}
            </button>
            {pauseMsg}
          </>
        ) : (
          <span className="text-muted" style={muted}>only a Global Admin can flip it</span>
        )}
        <span className="text-muted" style={{ fontSize: 12, flexBasis: '100%' }}>While this is OFF no rule fires, however many there are. Each rule has its own switch too. Pause the AI stops the service desk everywhere (mailboxes keep being read).</span>
        <div id="odau-heartbeat" style={{ flexBasis: '100%', fontSize: 12, marginTop: 2, color: (d.heartbeat ?? []).some((h) => h.stale) ? '#b42318' : '#6b7480' }}>
          Last tick:{' '}
          {(d.heartbeat ?? []).length === 0 ? (
            <span className="text-muted">not recorded yet</span>
          ) : (
            (d.heartbeat ?? []).map((h, i) => {
              const when = h.finished_at || h.started_at
              const time = when ? when.slice(11, 19) : '—'
              const ok = !h.last_error && !!h.last_ok_at && h.last_ok_at === h.finished_at
              const label = h.stale ? 'stale' : ok ? 'ok' : (h.last_error ? 'error' : '—')
              return (
                <span key={h.tick} style={{ color: h.stale ? '#b42318' : undefined }}>
                  {i > 0 ? ' · ' : ''}
                  {h.tick} {time} ({label})
                </span>
              )
            })
          )}
        </div>
        {d.isGlobal && (
          <>
            <div style={{ flexBasis: '100%', borderTop: '1px solid #eef0f2', paddingTop: 10, marginTop: 4, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ margin: 0, fontSize: 13 }}><strong>Notify email</strong></label>
              <input type="email" className="form-control" id="odau-notify" style={{ width: 280 }} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="who hears when Autonomy proposes or remediates" />
              <button type="button" className="btn btn-default btn-sm" id="odau-notify-save" onClick={() => post('/notify', { email }, sayNotify)}>Save</button>
              {notifyMsg}
              <span className="text-muted" style={muted}>empty = no email. Shadow never notifies; the log has all of it anyway.</span>
            </div>
            <div style={{ flexBasis: '100%', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
              <label style={{ margin: 0, fontSize: 13 }}><strong>Auto-diagnose</strong></label>
              <label style={{ margin: 0, fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" id="odau-diagnose" checked={d.autoDiagnose === true} disabled={d.autoDiagnose === null} onChange={(e) => post('/diagnose', { on: e.target.checked ? '1' : '0' }, sayDiag, () => onChange(), e.target.checked ? 'On' : 'Off')} />
                <span>critical alarms get a read-only diagnosis</span>
              </label>
              {diagMsg}
              <span className="text-muted" style={muted}>
                every minute with this page&apos;s cron, in the problem mode; at most 3 per company per minute, one per machine per hour.
                {d.autoDiagnose === null && <strong> The auto_diagnose column is missing.</strong>}
              </span>
            </div>
            <div style={{ flexBasis: '100%', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
              <label style={{ margin: 0, fontSize: 13 }}><strong>Daily action cap</strong> (per company)</label>
              <input type="number" min={0} className="form-control" id="odau-cap" style={{ width: 120 }} value={cap} disabled={d.dailyCap === null} onChange={(e) => setCap(e.target.value)} />
              <button type="button" className="btn btn-default btn-sm" id="odau-cap-save" disabled={d.dailyCap === null} onClick={() => post('/cap', { cap }, sayCap)}>Save</button>
              {capMsg}
              <span className="text-muted" style={muted}>playbook actions that are not shadow, per company, per day. 0 = no cap. A storm of alarms must not become a storm of restarts.</span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function SensorsPanel({ d }: { d: Page }) {
  const [msg, say] = useMsg()
  const [checked, setChecked] = useState<string[]>(d.sensors.filter((x) => Boolean(x.enabled)).map((x) => s(x.key)))
  return (
    <div className="panel panel-default">
      <div className="panel-body">
        <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14 }}>Sensors</strong>
          <span className="text-muted" style={muted}>
            states that become alarms - the machine went silent, a disk is almost full, the backup is missing. Each one writes ONE event when the state appears (module Opteemum, category Alarms, keyword = the sensor name) and one when it clears. Rules match them by keyword; critical ones get auto-diagnosed. Until this list is saved once, the quiet, cheap sensors run by default (silence, stopped service, disk full or filling, SMART, failed backup, critical patches); what you save here - even nothing - is what runs.
          </span>
        </div>
        {!d.sensorsReady ? (
          <div className="text-muted" style={{ fontSize: 12, marginTop: 8 }}>
            Needs the <code>2026_09_04_100001_autonomy_sensors</code> migration - apply it from <a href="/app/settings/change_log">Change Log &rarr; Database migrations</a>.
          </div>
        ) : (
          <>
            <table className="table table-condensed" style={{ fontSize: 12, margin: '10px 0 0' }}>
              <thead><tr><th style={{ width: 40 }}>On</th><th style={{ width: 200 }}>Sensor</th><th style={{ width: 80 }}>Severity</th><th>What it watches</th><th style={{ width: 90, textAlign: 'right' }}>Active now</th></tr></thead>
              <tbody>
                {d.sensors.map((x) => {
                  const key = s(x.key)
                  return (
                    <tr key={key}>
                      <td><input type="checkbox" className="odau-sensor" value={key} checked={checked.includes(key)} disabled={!d.isGlobal} onChange={(e) => setChecked(e.target.checked ? [...checked, key] : checked.filter((k) => k !== key))} /></td>
                      <td><strong>{s(x.label)}</strong><div><code style={{ fontSize: 11 }}>{key}</code> {x.slow ? <span className="text-muted" style={{ fontSize: 11 }}>hourly</span> : null}</div></td>
                      <td><span style={{ fontWeight: 600, color: SEV_COLOR[s(x.severity)] ?? '#6b7480' }}>{s(x.severity)}</span></td>
                      <td className="text-muted">{s(x.what)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{n(x.active) > 0 ? <strong>{n(x.active)}</strong> : <span className="text-muted">0</span>}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {d.isGlobal ? (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}>
                <button type="button" className="btn btn-primary btn-sm" id="odau-sensors-save" onClick={() => post('/sensors', { sensors: checked }, say)}>Save sensors</button>
                {msg}
                <span className="text-muted" style={muted}>A freshly turned-on sensor announces at most 50 machines per minute; the rest follow on the next ticks.</span>
              </div>
            ) : (
              <span className="text-muted" style={muted}>only a Global Admin can turn sensors on or off</span>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function LibraryPanel({ d, onChange }: { d: Page; onChange(): void }) {
  const mayWrite = d.isGlobal || d.canScope
  return (
    <div className="panel panel-default">
      <div className="panel-body">
        <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14 }}>Rule library</strong>
          <span className="text-muted" style={muted}>the rules that matter in an infrastructure, on the sensors above, with the right playbook. Added off and in shadow: read what they would have done for a few days, then turn them up.</span>
        </div>
        <table className="table table-condensed" style={{ fontSize: 12, margin: '10px 0 0' }}>
          <tbody>
            {Object.entries(d.library).map(([key, t]) => (
              <LibraryRow key={key} k={key} t={t} d={d} mayWrite={mayWrite} onChange={onChange} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function LibraryRow({ k, t, d, mayWrite, onChange }: { k: string; t: Row; d: Page; mayWrite: boolean; onChange(): void }) {
  const [addMsg, sayAdd] = useMsg()
  const [rbMsg, sayRb] = useMsg()
  const pb = (t.playbook ?? {}) as Row
  const runbooks = (t.runbooks ?? {}) as Record<string, [string, string]>
  return (
    <tr data-key={k}>
      <td style={{ width: 260 }}>
        <strong>{s(t.name)}</strong>
        <div><code style={{ fontSize: 11 }}>{s(t.sensor)}</code> <span className="text-muted">{s(t.severity)} &middot; {s(pb.action)}{s(t.command_type) !== '' ? ` ${s(t.command_type)}` : ''} &middot; else {s(pb.escalate)}</span></div>
      </td>
      <td className="text-muted">
        {s(t.why)}
        {Object.keys(runbooks).length > 0 && (
          <div style={{ marginTop: 4 }}>
            {Object.entries(runbooks).map(([os, rb]) => (
              <div key={os}><strong>{os.charAt(0).toUpperCase() + os.slice(1)}</strong> &middot; {rb[0]}: <code style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>{rb[1]}</code></div>
            ))}
            <span style={{ fontSize: 11 }}>
              {mayWrite && (
                <>
                  <button type="button" className="btn btn-default btn-xs odau-lib-runbooks" onClick={() => post('/library/runbooks', { key: k, company_id: String(d.companyId) }, sayRb, undefined, 'In the catalog')}>Put them in the catalog, incident-safe</button>{' '}
                  {rbMsg} &middot;{' '}
                </>
              )}
              or copy them into the vetted catalog by hand and mark them incident-safe under Settings &rarr; AI Agent &rarr; Runbooks. Once there, the rule, the alarm response and the service desk can run them.
            </span>
          </div>
        )}
      </td>
      <td style={{ width: 110, whiteSpace: 'nowrap', textAlign: 'right' }}>
        {mayWrite && (
          <>
            <button type="button" className="btn btn-default btn-xs odau-lib-add" onClick={() => post('/library/add', { key: k, company_id: String(d.companyId) }, sayAdd, () => setTimeout(onChange, 700), 'Added (off, shadow)')}>Add</button>
            <div style={{ fontSize: 11, marginTop: 3 }}>{addMsg}</div>
          </>
        )}
      </td>
    </tr>
  )
}

function RuleCard({ d, rule, onEdit, onChange }: { d: Page; rule: Row; onEdit(): void; onChange(): void }) {
  const [msg, say] = useMsg()
  const id = n(rule.ID)
  const ruleCompany = n(rule.company_id)
  const isOwn = ruleCompany > 0 && ruleCompany === d.companyId
  const mayEdit = d.isGlobal || isOwn
  const ov = isOwn ? null : (d.overrides[String(id)] ?? null)
  const effPosture = ov && ['shadow', 'propose', 'remediate'].includes(s(ov.posture)) ? s(ov.posture) : s(rule.posture)
  const pm = POSTURE[effPosture] ?? POSTURE.shadow
  const offHere = Boolean(ov) && n(ov?.is_off) === 1
  const [ovOff, setOvOff] = useState(offHere)
  const [ovPosture, setOvPosture] = useState(ov ? s(ov.posture) : '')
  const win: string[] = []
  if (s(rule.active_days)) {
    const names: Record<string, string> = Object.fromEntries(DAYS)
    win.push(s(rule.active_days).split(',').map((x) => names[x] ?? x).join('/'))
  }
  if (s(rule.active_from) && s(rule.active_to)) win.push(`${s(rule.active_from).slice(0, 5)}–${s(rule.active_to).slice(0, 5)}`)

  return (
    <div className="panel panel-default">
      <div className="panel-body">
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 14 }}>{s(rule.name)}</strong>
              <span style={pill(pm[2], pm[1])}>{pm[0]}</span>
              {ruleCompany === 0 ? (
                <span title="A platform rule - it applies to every company" style={{ ...pill('#eef0f2', '#3d4752'), fontWeight: 400 }}><i className="fas fa-globe" aria-hidden="true"></i> the whole platform</span>
              ) : isOwn ? (
                <span title="Your company's rule - you can change it directly" style={{ ...pill('#e3f0fa', '#1f5f8b'), fontWeight: 400 }}>{d.companyName}</span>
              ) : (
                <span style={{ ...pill('#eef0f2', '#6b7480'), fontWeight: 400 }}>company #{ruleCompany}</span>
              )}
              {n(rule.enabled) !== 1 ? (
                <span style={{ ...pill('#eef0f2', '#6b7480'), fontWeight: 400 }}>disabled globally</span>
              ) : offHere ? (
                <span style={{ ...pill('#fdecea', '#b42318'), fontWeight: 400 }}>off for this company</span>
              ) : (
                <span style={{ ...pill('#e6f4e6', '#2e7d32'), fontWeight: 400 }}>live here</span>
              )}
            </div>
            <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
              when <strong>{s(rule.match_severity)}</strong>
              {s(rule.match_module) && <> · {s(rule.match_module)}</>}
              {s(rule.match_category) && <>/ {s(rule.match_category)}</>}
              {s(rule.match_keyword) && <> · &ldquo;{s(rule.match_keyword)}&rdquo;</>}
              {' '}&middot; {n(rule.threshold_count)}&times; in {n(rule.threshold_minutes)}min &rarr; run <code>{s(rule.command_type)}</code>
              {s(rule.playbook_summary) && <><br /><span style={{ color: '#1f5f8b' }}>playbook:</span> {s(rule.playbook_summary)}</>}
              {' '}&middot; cooldown {n(rule.cooldown_minutes)}min, max {n(rule.max_per_hour)}/h, {n(rule.max_devices_per_run)}/run
              {win.length > 0 && <> &middot; <span style={{ color: '#8a6116' }}>window: {win.join(' ')}</span></>}
              {s(rule.description) && <div>{s(rule.description)}</div>}
            </div>
            {!isOwn && (
              <div style={{ marginTop: 10, padding: '8px 10px', background: '#f6f7f9', borderRadius: 6, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>For {d.companyName}:</span>
                <label style={{ margin: 0, fontSize: 12, fontWeight: 400 }}><input type="checkbox" className="odau-ov-off" checked={ovOff} onChange={(e) => setOvOff(e.target.checked)} /> off here</label>
                <label style={{ margin: 0, fontSize: 12, fontWeight: 400 }}>
                  posture:{' '}
                  <select className="odau-ov-posture" style={{ fontSize: 12 }} value={ovPosture} onChange={(e) => setOvPosture(e.target.value)}>
                    <option value="">inherit ({s(rule.posture)})</option>
                    <option value="shadow">shadow</option>
                    <option value="propose">propose</option>
                    <option value="remediate">remediate</option>
                  </select>
                </label>
                <button type="button" className="btn btn-default btn-xs odau-ov-save" onClick={() => post('/rules/override', { rule_id: String(id), is_off: ovOff ? '1' : '0', posture: ovPosture }, say, () => onChange())}>Save for company</button>
                {msg}
              </div>
            )}
          </div>
          {mayEdit && (
            <div style={{ flex: '0 0 auto', textAlign: 'right' }}>
              <button type="button" className="btn btn-default btn-xs odau-edit" onClick={onEdit}>Edit</button>{' '}
              <button type="button" className="btn btn-default btn-xs odau-del" onClick={() => {
                if (!window.confirm('Delete the global rule? (per-company overrides go with it; the history stays)')) return
                post('/rules/delete', { id: String(id) }, say, () => onChange(), 'Deleted')
              }}>Delete</button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function RuleEditor({ d, rule, onClose, onSaved }: { d: Page; rule: Row; onClose(): void; onSaved(): void }) {
  let pb: Row = {}
  try { pb = (JSON.parse(s(rule.playbook_json) || '{}') || {}) as Row } catch { pb = {} }
  const f = (k: string, def: string) => (rule[k] === undefined || rule[k] === null ? def : s(rule[k]))
  const [v, setV] = useState<Record<string, string>>({
    id: f('ID', '0'), name: f('name', ''), description: f('description', ''), enabled: n(rule.enabled) === 1 ? '1' : '0',
    posture: f('posture', 'shadow'), match_module: f('match_module', ''), match_category: f('match_category', ''),
    match_severity: f('match_severity', 'critical'), match_keyword: f('match_keyword', ''),
    threshold_count: f('threshold_count', '1'), threshold_minutes: f('threshold_minutes', '15'), command_type: f('command_type', ''),
    cooldown_minutes: f('cooldown_minutes', '60'), max_per_hour: f('max_per_hour', '5'), max_devices_per_run: f('max_devices_per_run', '5'),
    active_days: f('active_days', ''), active_from: f('active_from', '').slice(0, 5), active_to: f('active_to', '').slice(0, 5),
    pb_on: String(pb.on) === '1' ? '1' : '0', pb_diagnose: pb.diagnose === undefined ? '1' : String(pb.diagnose) === '1' ? '1' : '0',
    pb_action: s(pb.action) || 'catalog', pb_verify: pb.verify_minutes === undefined ? '10' : s(pb.verify_minutes), pb_escalate: s(pb.escalate) || 'ticket',
    pb_servers: pb.protect_servers === undefined ? '1' : String(pb.protect_servers) === '1' ? '1' : '0',
    pb_rb_win: s(pb.runbook_windows) || '0', pb_rb_lin: s(pb.runbook_linux) || '0',
    pb_deps: pb.need_dependents === undefined ? '1' : String(pb.need_dependents) === '1' ? '1' : '0',
  })
  const [msg, say] = useMsg()
  const [preview, setPreview] = useState<ReactNode>(null)
  const set = (k: string, val: string) => setV({ ...v, [k]: val })
  const days = v.active_days ? v.active_days.split(',') : []
  const fields = (): Record<string, PostValue> => ({
    id: v.id, name: v.name, description: v.description, enabled: v.enabled, posture: v.posture,
    match_module: v.match_module, match_category: v.match_category, match_severity: v.match_severity, match_keyword: v.match_keyword,
    threshold_count: v.threshold_count, threshold_minutes: v.threshold_minutes, command_type: v.command_type, cooldown_minutes: v.cooldown_minutes,
    max_per_hour: v.max_per_hour, max_devices_per_run: v.max_devices_per_run, active_days: v.active_days, active_from: v.active_from, active_to: v.active_to,
    'playbook[on]': v.pb_on, 'playbook[diagnose]': v.pb_diagnose, 'playbook[action]': v.pb_action, 'playbook[verify_minutes]': v.pb_verify,
    'playbook[escalate]': v.pb_escalate, 'playbook[protect_servers]': v.pb_servers, 'playbook[runbook_windows]': v.pb_rb_win,
    'playbook[runbook_linux]': v.pb_rb_lin, 'playbook[need_dependents]': v.pb_deps,
  })
  const input = (k: string, type = 'text', extra: Record<string, unknown> = {}) => <input type={type} className="form-control" id={`odau-f-${k}`} value={v[k]} onChange={(e) => set(k, e.target.value)} {...extra} />
  const check = (k: string, id: string) => <input type="checkbox" id={id} checked={v[k] === '1'} onChange={(e) => set(k, e.target.checked ? '1' : '0')} />

  return (
    <div className="panel panel-default" id="odau-editor" style={{ borderLeft: '4px solid #1f5f8b' }}>
      <div className="panel-body">
        <div className="row">
          <div className="form-group col-md-6"><label>Name</label>{input('name')}</div>
          <div className="form-group col-md-6"><label>Description</label>{input('description')}</div>
        </div>
        <div className="row">
          <div className="form-group col-md-3"><label>Severity</label>
            <select className="form-control" id="odau-f-sev" value={v.match_severity} onChange={(e) => set('match_severity', e.target.value)}>
              <option value="critical">critical</option><option value="major">major</option><option value="minor">minor</option><option value="info">info</option>
            </select></div>
          <div className="form-group col-md-3"><label>Module</label>{input('match_module', 'text', { placeholder: 'any' })}</div>
          <div className="form-group col-md-3"><label>Category</label>{input('match_category', 'text', { placeholder: 'any' })}</div>
          <div className="form-group col-md-3"><label>Keyword</label>{input('match_keyword', 'text', { placeholder: 'any' })}</div>
        </div>
        <div className="row">
          <div className="form-group col-md-3"><label>Threshold count</label>{input('threshold_count', 'number', { min: 1 })}</div>
          <div className="form-group col-md-3"><label>Within minutes</label>{input('threshold_minutes', 'number', { min: 1 })}</div>
          <div className="form-group col-md-3"><label>Action (incident_safe)</label>
            <select className="form-control" id="odau-f-cmd" value={v.command_type} onChange={(e) => set('command_type', e.target.value)}>
              <option value="">-- pick --</option>
              {d.catalog.map((c) => <option key={s(c.command_type)} value={s(c.command_type)}>{s(c.command_type)} — {s(c.command_title)}</option>)}
            </select></div>
          <div className="form-group col-md-3"><label>Posture</label>
            <select className="form-control" id="odau-f-posture" value={v.posture} onChange={(e) => set('posture', e.target.value)}>
              <option value="shadow">shadow (log only)</option><option value="propose">propose (human approves)</option><option value="remediate">remediate (run now)</option>
            </select></div>
        </div>
        <div className="row">
          <div className="form-group col-md-3"><label>Cooldown (min)</label>{input('cooldown_minutes', 'number', { min: 1 })}</div>
          <div className="form-group col-md-3"><label>Max per hour</label>{input('max_per_hour', 'number', { min: 1 })}</div>
          <div className="form-group col-md-3"><label>Max devices / run</label>{input('max_devices_per_run', 'number', { min: 1 })}</div>
          <div className="form-group col-md-3"><label style={{ display: 'block' }}>Enabled</label>{check('enabled', 'odau-f-enabled')} <span className="text-muted" style={muted}>globally</span></div>
        </div>
        <div className="row">
          <div className="form-group col-md-6">
            <label>Maintenance window &mdash; days</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
              {DAYS.map(([num, lbl]) => (
                <label key={num} style={{ margin: 0, fontWeight: 400 }}>
                  <input type="checkbox" className="odau-f-day" value={num} checked={days.includes(num)} onChange={(e) => set('active_days', (e.target.checked ? [...days, num] : days.filter((x) => x !== num)).sort().join(','))} /> {lbl}
                </label>
              ))}
            </div>
            <span className="text-muted" style={{ fontSize: 11 }}>nothing ticked = any day</span>
          </div>
          <div className="form-group col-md-3"><label>From (HH:MM)</label>{input('active_from', 'time')}</div>
          <div className="form-group col-md-3"><label>To (HH:MM)</label>{input('active_to', 'time')}</div>
          <div className="col-md-12"><span className="text-muted" style={{ fontSize: 11 }}>Empty hours = all day. From &gt; To crosses midnight (e.g. 22:00&ndash;06:00). The window is in server time.</span></div>
        </div>
        <div className="row" style={{ borderTop: '1px solid #eef0f2', marginTop: 4, paddingTop: 10 }}>
          <div className="col-md-12" style={{ marginBottom: 6 }}>
            <label style={{ margin: 0 }}><input type="checkbox" id="odau-f-pb-on" checked={v.pb_on === '1'} disabled={!d.playbookReady} onChange={(e) => set('pb_on', e.target.checked ? '1' : '0')} /> <strong>Playbook</strong></label>{' '}
            <span className="text-muted" style={muted}>
              diagnose &rarr; act (with guards) &rarr; verify on the next ticks &rarr; escalate if it persists; twice failed on a machine in a day = the rule stops there.
              {!d.playbookReady && <strong> Needs the 2026_09_04_100002_autonomy_playbook migration.</strong>}
            </span>
          </div>
          <div className="form-group col-md-3"><label style={{ display: 'block' }}>Diagnose first</label>{check('pb_diagnose', 'odau-f-pb-diag')} <span className="text-muted" style={muted}>the agent, read-only</span></div>
          <div className="form-group col-md-3"><label>Action</label>
            <select className="form-control" id="odau-f-pb-action" value={v.pb_action} onChange={(e) => set('pb_action', e.target.value)}>
              <option value="catalog">catalog command (above)</option><option value="runbook">a runbook from the vetted catalog, by OS</option><option value="agent">the agent fixes it (incident-safe)</option><option value="none">diagnosis only</option>
            </select></div>
          <div className="form-group col-md-3"><label>Verify after (min)</label>{input('pb_verify', 'number', { min: 0 })}</div>
          <div className="form-group col-md-3"><label>If not fixed</label>
            <select className="form-control" id="odau-f-pb-esc" value={v.pb_escalate} onChange={(e) => set('pb_escalate', e.target.value)}>
              <option value="ticket">open a ticket</option><option value="propose">propose it (human approves)</option><option value="notify">mail the notify address</option><option value="none">nothing</option>
            </select></div>
          {v.pb_action === 'runbook' && (
            <>
              <div className="form-group col-md-6 odau-pb-rb"><label>Runbook on Windows</label>
                <select className="form-control" id="odau-f-pb-rb-win" value={v.pb_rb_win} onChange={(e) => set('pb_rb_win', e.target.value)}>
                  <option value="0">-- pick an incident-safe runbook --</option>
                  {d.runbooks.map((rb) => <option key={n(rb.ID)} value={String(n(rb.ID))}>{s(rb.command_title)}</option>)}
                </select></div>
              <div className="form-group col-md-6 odau-pb-rb"><label>Runbook on Linux / macOS</label>
                <select className="form-control" id="odau-f-pb-rb-lin" value={v.pb_rb_lin} onChange={(e) => set('pb_rb_lin', e.target.value)}>
                  <option value="0">-- pick an incident-safe runbook --</option>
                  {d.runbooks.map((rb) => <option key={n(rb.ID)} value={String(n(rb.ID))}>{s(rb.command_title)}</option>)}
                </select>
                <span className="text-muted" style={{ fontSize: 11 }}>runbooks are marked incident-safe under <a href="/app/settings/ai_agent#aiaRunbooks">Settings &rarr; AI Agent &rarr; Runbooks</a></span></div>
            </>
          )}
          <div className="col-md-12" style={{ fontSize: 12 }}>
            <label style={{ fontWeight: 400, marginRight: 14 }}>{check('pb_servers', 'odau-f-pb-servers')} on servers and hosts, never remediate - propose</label>
            <label style={{ fontWeight: 400 }}>{check('pb_deps', 'odau-f-pb-deps')} if the CMDB says something depends on the machine, propose instead of remediate</label>
            <div className="text-muted" style={{ marginTop: 4 }}>Catalog commands may carry <code>{'{{service}}'}</code> or <code>{'{{mount}}'}</code>: the playbook fills them from the sensor alarm (e.g. the stopped service&apos;s name).</div>
          </div>
        </div>
        {d.catalog.length === 0 && (
          <div className="text-muted" style={{ fontSize: 12, marginBottom: 8 }}>
            No incident_safe action in the catalog yet - mark one under <a href="/app/settings/ai_agent">Settings &rarr; AI Agent</a> so a rule has something to aim at.
          </div>
        )}
        <button type="button" className="btn btn-primary btn-sm" id="odau-save" onClick={() => post('/rules', fields(), say, () => onSaved(), 'Saved')}>Save rule</button>{' '}
        <button type="button" className="btn btn-default btn-sm" id="odau-preview" onClick={() => {
          setPreview(<span className="text-muted">Looking...</span>)
          void autonomyPost('/rules/preview', fields()).then((r) => {
            if (!r.ok) { setPreview(<span style={{ color: '#b42318' }}>{r.error || 'Preview failed.'}</span>); return }
            if (!r.count) { setPreview(<span className="text-muted">No machine matches this rule right now (within the threshold window).</span>); return }
            setPreview(<>
              <div style={{ marginBottom: 6 }}><strong>{r.count}</strong> machine(s) would match right now:</div>
              {(r.hits ?? []).map((h) => <div key={h.device_id}>· {h.device || `#${h.device_id}`} <span className="text-muted">({h.n} events)</span></div>)}
            </>)
          }).catch(() => setPreview(<span style={{ color: '#b42318' }}>Cererea a picat.</span>))
        }}>Preview matches now</button>{' '}
        <button type="button" className="btn btn-default btn-sm" id="odau-cancel" onClick={onClose}>Cancel</button>{' '}
        <span style={{ marginLeft: 8 }}>{msg}</span>
        {preview && <div id="odau-preview-out" style={{ marginTop: 10, fontSize: 12, background: '#f6f7f9', borderRadius: 6, padding: '8px 10px', maxHeight: 220, overflow: 'auto' }}>{preview}</div>}
      </div>
    </div>
  )
}
